// useState/useEffect went with the facility onboarding dialog below.
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
// import StatusBoardPage from "pages/dashboard/facility/StatusBoardPage";
// Virtual Queue + Virtual Registration send patient PHI (phone numbers) over SMS
// via Twilio. The nav links and routes below are gated to non-prod
// (isDevEnvironment) until the Twilio BAA is signed. Keep the gate in place
// until then — do not expose these in production.
import WaitingRoom from "components/queue/WaitingRoom";
// import SchedulerPage from "pages/dashboard/facility/SchedulerPage";
// TODO: Messages (facility <> patient comms) disabled for beta launch — re-enable
// when the patient half ships. See the nav entry in DrawerComponents.js.
// import ClinicMessagingPage from "pages/dashboard/facility/ClinicMessagingPage";
import VisitStartCheckInsPage from "pages/dashboard/facility/VisitStartCheckInsPage";
import VirtualRegistrationPage from "pages/dashboard/facility/VirtualRegistrationPage";
import Settings from "components/dashboard/Settings";
// TODO: Advertise hidden for now — re-enable when ready. See the nav entry in
// DrawerComponents.js.
// import AuctionPage from "pages/dashboard/AuctionPage";
import UpgradePage from "pages/dashboard/UpgradePage";
import DebugInstallPage from "pages/dashboard/DebugInstallPage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import { useFacility } from "hooks/useFacility";
import { useAuth } from "hooks/useAuth";
import { isDevEnvironment } from "utils/isDevEnvironment";
import QueueDisplay from "components/queue/splitflap/QueueDisplay";
import DashboardLayout from "components/dashboard/DashboardLayout";
// import FacilityOnboardingDialog from "components/dashboard/FacilityOnboardingDialog";
// import FacilityChecklist from "components/dashboard/FacilityChecklist";
// import { db } from "services/firebase"; // only the onboarding check read it
import { PromptsManagerProvider } from "context/PromptsManager";
import { VisitStartCheckInsProvider } from "context/VisitStartCheckIns";
import HomePage from "pages/dashboard/HomePage";

const DashboardRoutes = () => {
  const { data, setData } = useFacility();
  const { user, subscription, isGlobalAdmin } = useAuth();
  const location = useLocation();
  // Mounted here rather than inside VisitStartCheckInsPage itself, so its
  // subscriptions survive navigating to another dashboard page and back.
  const clinicId = data?.id ? String(data.id) : null;
  // PHI over SMS — Virtual Queue/Registration stay non-prod until the Twilio BAA.
  const showVirtualQueue = isDevEnvironment();
  const isBoard =
    showVirtualQueue && location.pathname === "/dashboard/queue/board";
  /*
   * TODO: facility onboarding is held back with the rest of the role.
   *
   * The dialog sold Status Board, Advertise and CareMap Plus, and none of the
   * three is reachable now. Its checklist pointed at /dashboard/status, which
   * has never been a route in this file, so those rows were dead before any of
   * this.
   *
   * The Firestore read below ran on every dashboard mount, for every role, only
   * to decide whether a facility saw the dialog. It goes with it.
   *
   * Restore alongside the imports at the top of this file and the render in
   * DashboardLayout:
   *
   * const [showFacilityOnboarding, setShowFacilityOnboarding] = useState(false);
   *
   * useEffect(() => {
   *   const checkOnboardingStatus = async () => {
   *     if (user?.uid) {
   *       try {
   *         const userDoc = await db.collection("users").doc(user.uid).get();
   *         const userData = userDoc.data();
   *
   *         if (
   *           userData?.role === "facility" &&
   *           !userData?.hideFacilityOnboardingDialog
   *         ) {
   *           setShowFacilityOnboarding(true);
   *         }
   *       } catch (error) {
   *         console.error("Error checking onboarding status:", error);
   *       }
   *     }
   *   };
   *
   *   checkOnboardingStatus();
   * }, [user]);
   *
   * const handleCloseFacilityOnboarding = () => {
   *   setShowFacilityOnboarding(false);
   * };
   */

  return (
    <DashboardLayout fullWidth={isBoard}>
      {/* TODO: held back with the facility role. FacilityChecklist goes too:
          it only ever rendered for a facility that had already dismissed the
          dialog, so leaving it would strand those accounts with a checklist
          whose rows point at /dashboard/status and /dashboard/queue, neither of
          which they can reach. Restore both together:

      {!isBoard && <FacilityChecklist locationData={data} />}

      <FacilityOnboardingDialog
        open={showFacilityOnboarding}
        onClose={handleCloseFacilityOnboarding}
        userId={user?.uid}
        locationData={data}
      />
      */}
      <PromptsManagerProvider>
        <VisitStartCheckInsProvider clinicId={clinicId}>
          <Routes>
            {/* TODO: the facility role is held back for now. Its landing page
                was the Status Board; a clinic now lands on Home (HomePage),
                which leads to Check-ins, the only facility surface still on. The Status Board nav
                entry is commented out in DrawerComponents.js. Restore the two
                together, and the import at the top of this file:

            <Route exact path="/" element={<StatusBoardPage data={data} />} />
            */}
            <Route exact path="/" element={<HomePage />} />
            {/* Virtual Queue/Registration: PHI over SMS — non-prod only until Twilio BAA. */}
            {showVirtualQueue && (
              <Route
                path="/queue/board"
                element={<QueueDisplay queue={data.queue} />}
              />
            )}
            {showVirtualQueue && (
              <Route
                path="/virtual-registration"
                element={<VirtualRegistrationPage data={data} setData={setData} />}
              />
            )}
            {showVirtualQueue && (
              <Route
                path="/queue"
                element={<WaitingRoom data={data} setData={setData} />}
              />
            )}
            {/* TODO: Wait Time Scheduler disabled for beta launch. It is a
                premium feature behind PremiumWrapper, and paid upgrading is
                hidden, so the page could only ever show its preview. Hidden
                alongside it: the Schedule entry in FacilityDrawer
                (DrawerComponents.js), and the "use the Schedule tool" sentence
                in WaitTimeForm.js and AdminTimeForm.js. Restore them together.
                SchedulerPage itself is untouched. */}
            {/* <Route
              path="/schedule"
              element={<SchedulerPage data={data} setData={setData} />}
            /> */}
            {/* TODO: Messages (facility <> patient comms) disabled for beta launch.
                sendClinicBroadcast writes to users/{uid}/clinicMessages and nothing
                reads it — the patient's MessagesPage is commented out in
                PatientRoutes.jsx for the same reason. Re-enable both together. */}
            {/* <Route
              path="/messaging"
              element={<ClinicMessagingPage data={data} setData={setData} />}
            /> */}
            {/* VisitStart: the clinic's intake. One page, not two — a check-in, the
                code and the settings are all dialogs on top of the inbox, not
                screens of their own: a clinic opens them about twice, and giving
                each a route put them in the nav's way every day. The sessionId
                route stays a real permalink (opens the same page with its dialog
                already open) so a link to one patient's check-in still works. */}
            <Route
              path="/visitstart"
              element={<VisitStartCheckInsPage data={data} />}
            />
            <Route
              path="/visitstart/:sessionId"
              element={<VisitStartCheckInsPage data={data} />}
            />
            <Route
              path="/settings"
              element={<Settings uid={user.uid} subscription={subscription} />}
            />
            <Route path="/upgrade" element={<UpgradePage />} />
            {/* Not linked from any nav — see DebugInstallPage.jsx. */}
            <Route path="/debug-install" element={<DebugInstallPage />} />

            {/* Global-admin-only routes */}
            <Route
              path="/admin/approval"
              element={
                isGlobalAdmin ? (
                  <ApprovalPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/admin/assistants"
              element={
                isGlobalAdmin ? (
                  <AssistantManagerPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/admin/usage"
              element={
                isGlobalAdmin ? (
                  <UsageMonitorPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/prompts"
              element={
                isGlobalAdmin ? (
                  <PromptsManagerPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            {/* <Route path="/advertising" element={<AuctionPage />} /> */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </VisitStartCheckInsProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default DashboardRoutes;
